import type { MarketOverview, SystemStatus } from "@/api/types";
import StatusBadges from "@/components/StatusBadges";
interface PulseBarProps {
  marketOverview: MarketOverview | null;
  latestScanAt: number | null;
  killSwitchTriggered: boolean;
  systemStatus: SystemStatus | null;
}
function timeAgo(timestampMs: number | null): string {
  if (!timestampMs) return "no scan yet";
  const seconds = Math.max(0, Math.floor((Date.now() - timestampMs) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
function fearGreedLabel(value: number): string {
  if (value <= 24) return "Extreme Fear";
  if (value <= 44) return "Fear";
  if (value <= 55) return "Neutral";
  if (value <= 75) return "Greed";
  return "Extreme Greed";
}
function fearGreedColor(value: number): string {
  if (value <= 44) return "text-sell";
  if (value <= 55) return "text-ink-dim";
  return "text-gold";
}
export default function PulseBar({ marketOverview, latestScanAt, killSwitchTriggered, systemStatus }: PulseBarProps) {
  return (
    <div className="relative overflow-hidden border-b border-line bg-panel/80">
      {killSwitchTriggered && <div className="absolute inset-0 bg-sell/10" aria-hidden="true" />}
      <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 pt-2.5 sm:px-6">
        <StatusBadges systemStatus={systemStatus} />
      </div>
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center gap-x-8 gap-y-2 px-4 py-3 text-xs sm:px-6">
        <div className="flex items-center gap-2 font-mono text-ink-dim">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-turquoise opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-turquoise" />
          </span>
          <span>Scanner live · {timeAgo(latestScanAt)}</span>
        </div>

        {marketOverview && (
          <>
            <div className="flex items-center gap-2 font-mono">
              <span className="text-ink-faint">Fear &amp; Greed</span>
              <span className={`font-semibold ${fearGreedColor(marketOverview.fearGreedIndex)}`}>
                {marketOverview.fearGreedIndex} · {fearGreedLabel(marketOverview.fearGreedIndex)}
              </span>
            </div>

            <div className="flex items-center gap-2 font-mono">
              <span className="text-ink-faint">BTC Dominance</span>
              <span className="font-semibold text-ink">{marketOverview.btcDominancePercent.toFixed(1)}%</span>
              <span
                className={
                  marketOverview.btcDominanceTrend === "up"
                    ? "text-gold"
                    : marketOverview.btcDominanceTrend === "down"
                      ? "text-sell"
                      : "text-ink-faint"
                }
              >
                {marketOverview.btcDominanceTrend === "up"
                  ? "▲"
                  : marketOverview.btcDominanceTrend === "down"
                    ? "▼"
                    : "—"}
              </span>
            </div>

            <div className="flex items-center gap-2 font-mono">
              <span className="text-ink-faint">Total Cap 24h</span>
              <span
                className={`font-semibold ${marketOverview.totalMarketCapChange24hPercent >= 0 ? "text-profit" : "text-sell"}`}
              >
                {marketOverview.totalMarketCapChange24hPercent >= 0 ? "+" : ""}
                {marketOverview.totalMarketCapChange24hPercent.toFixed(2)}%
              </span>
            </div>

            {marketOverview.trendingCoins.length > 0 && (
              <div className="flex items-center gap-2 font-mono text-ink-faint">
                <span>Trending</span>
                <span className="text-turquoise">{marketOverview.trendingCoins.slice(0, 4).join(" · ")}</span>
              </div>
            )}
          </>
        )}

        {killSwitchTriggered && (
          <div className="ml-auto flex items-center gap-2 font-mono font-semibold uppercase tracking-wider text-sell">
            <span className="h-2 w-2 animate-pulse-dot rounded-full bg-sell" />
            Kill switch active — new entries halted
          </div>
        )}
      </div>
    </div>
  );
}
